import { useEffect } from 'react';

import type { TronWeb } from 'tronweb';
import { useAccount, useBalance } from 'wagmi';

import { Currency } from '@/constants';
import { useBtcWallet } from '@/networks/btc-like/btc';
import { SignPsbtOptions } from '@/networks/btc-like/common';
import { useDogeWallet } from '@/networks/btc-like/doge';
import { BtcLikeWalletBalance, useBtcLikeBalance } from '@/networks/btc-like/hooks';
import { useLtcWallet } from '@/networks/btc-like/ltc';
import { useCoinwebBalance, useCoinwebWallet } from '@/networks/coinweb/hooks';
import { useConnectEvmWallet } from '@/networks/evm/hooks';
import { useTronWallet } from '@/networks/tron';
import { BtcLikeCurrency, Trc20Currency, TronCurrency } from '@/types';
import {
  convertBigIntToString,
  formatNumberWithPrecision,
  isBtcCurrency,
  isErc20Currency,
  isEvmCurrency,
  isCwebCurrency,
  isTronCurrency,
  isTrc20Currency,
  isLtcCurrency,
  isDogeCurrency,
  decimalsForCurrency,
} from '@/utils';
import Big from '@/utils/Bigjs';

import useChainByCurrency from './useChainByCurrency';
import useL1TokenContractParams from './useL1TokenContractParams';
import useReadERC20Balance from './useReadERC20Balance';
import useReadTRC20Balance from './useReadTRC20Balance';
import { useTokenUsdPrices } from './useTokenUsdPrices';

const BALANCE_FIAT_PRECISION = 2;

export type WalletBalance<TCurrency extends Currency = Currency> = {
  balanceToken: bigint;
  balanceUsd: string;
  isLoadingBalance: boolean;
} & (TCurrency extends BtcLikeCurrency ? { data: BtcLikeWalletBalance } : {});

export type WalletConnectionDefault = {
  isConnected: boolean;
  address: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  isConnecting?: boolean;
};

export type WalletTronWeb = {
  tronWeb: TronWeb;
};

export type WalletBtcLike = {
  publicKey: string | null;
  signPsbt: (
    psbtBase64: string,
    options: SignPsbtOptions
  ) => Promise<{
    signedBase64Psbt: string;
    txId: string | null;
  }>;
};

export type WalletConnection<TCurrency extends Currency = Currency> = WalletConnectionDefault &
  (TCurrency extends BtcLikeCurrency ? WalletBtcLike : {}) &
  (TCurrency extends TronCurrency | Trc20Currency ? WalletTronWeb : {});

export type Wallet<TCurrency extends Currency = Currency> = WalletBalance<TCurrency> & WalletConnection<TCurrency>;

function useBtcWalletStrategy(): () => Wallet {
  const { address, isConnected, connect, disconnect, publicKey, signPsbt } = useBtcWallet();
  const { data: balance, isLoading } = useBtcLikeBalance(Currency.BTC, address);
  const { getTokenUsdPrice } = useTokenUsdPrices();

  const usdVal = Big(balance.human).mul(getTokenUsdPrice(Currency.BTC)).toString();

  return () => ({
    balanceToken: balance.raw ?? 0n,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    data: balance,
    isLoadingBalance: isLoading,
    isConnected,
    address,
    connect,
    disconnect,
    publicKey,
    signPsbt,
  });
}

function useLtcWalletStrategy(): () => Wallet {
  const { address, isConnected, connect, disconnect, publicKey, signPsbt } = useLtcWallet();
  const { data: balance, isLoading } = useBtcLikeBalance(Currency.LTC, address);
  const { getTokenUsdPrice } = useTokenUsdPrices();

  const usdVal = Big(balance.human).mul(getTokenUsdPrice(Currency.LTC)).toString();

  return () => ({
    balanceToken: balance.raw ?? 0n,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    data: balance,
    isLoadingBalance: isLoading,
    isConnected,
    address,
    connect,
    disconnect,
    publicKey,
    signPsbt,
  });
}

function useDogeWalletStrategy(): () => Wallet {
  const { address, isConnected, connect, disconnect, publicKey, signPsbt } = useDogeWallet();
  const { data: balance, isLoading } = useBtcLikeBalance(Currency.DOGE, address);
  const { getTokenUsdPrice } = useTokenUsdPrices();

  const usdVal = Big(balance.human).mul(getTokenUsdPrice(Currency.DOGE)).toString();

  return () => ({
    balanceToken: balance.raw ?? 0n,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    data: balance,
    isLoadingBalance: isLoading,
    isConnected,
    address,
    connect,
    disconnect,
    publicKey,
    signPsbt,
  });
}

function useTronWalletStrategy(): () => Wallet {
  const { balance, isLoadingBalance, isConnected, address, connect, disconnect, tronWeb } = useTronWallet();
  const { getTokenUsdPrice } = useTokenUsdPrices();

  const usdVal = Big(Number(balance))
    .div(10 ** decimalsForCurrency(Currency.TRX))
    .mul(getTokenUsdPrice(Currency.TRX))
    .toString();

  return () => ({
    tronWeb,
    balanceToken: BigInt(balance || 0n),
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    data: balance,
    isLoadingBalance,
    isConnected,
    address,
    connect,
    disconnect,
  });
}

function useCwebWalletStrategy(): () => Wallet {
  const { getTokenUsdPrice } = useTokenUsdPrices();
  const { isConnected, address, connect, disconnect, isConnecting } = useCoinwebWallet();
  const { data: balanceTokenRaw, isLoading } = useCoinwebBalance();

  const balanceToken = BigInt(balanceTokenRaw?.balanceHexCWEB ?? 0n);

  const usdVal = Big(Number(balanceToken))
    .div(10 ** decimalsForCurrency(Currency.CWEB))
    .mul(getTokenUsdPrice(Currency.CWEB))
    .toString();

  return () => ({
    balanceToken,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    isLoadingBalance: isLoading,
    isConnected,
    address,
    connect,
    disconnect,
    isConnecting,
  });
}

function useEvmWalletStrategy(currency: Currency): () => Wallet {
  const { getTokenUsdPrice } = useTokenUsdPrices();
  const { address = '0x0', isConnected } = useAccount();
  const { connect: connectEvm, disconnect } = useConnectEvmWallet();
  const currencyChainData = useChainByCurrency(currency);
  const { data: balance, isLoading } = useBalance({
    address,
    chainId: currencyChainData.chainData?.id,
    query: {
      enabled: isConnected,
      refetchInterval: 10 * 1000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  });

  const balanceRaw = balance?.value || 0n;
  const balanceHuman = convertBigIntToString(balanceRaw, currency);

  const usdVal = Big(balanceHuman).mul(getTokenUsdPrice(currency)).toString();

  return () => ({
    balanceToken: balanceRaw,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    isLoadingBalance: isLoading,
    isConnected,
    address,
    connect: () => Promise.resolve(connectEvm()),
    disconnect,
  });
}

function useEvmTokenWalletStrategy(token: Currency): () => Wallet {
  const { getTokenUsdPrice } = useTokenUsdPrices();
  const { address = '0x0', isConnected } = useAccount();
  const { connect: connectEvm, disconnect } = useConnectEvmWallet();
  const { getContractParams } = useL1TokenContractParams();
  const currencyChainData = useChainByCurrency(token);
  const { data: l1Erc20TokenBalance, getBalance, isLoading: isLoadingErc20Balance } = useReadERC20Balance(address);

  useEffect(() => {
    if (isErc20Currency(token)) {
      const { L1_TOKEN_ADDRESS } = getContractParams(token);
      getBalance(L1_TOKEN_ADDRESS, currencyChainData.chainData?.id);
    }
  }, [token, address, currencyChainData.chainData?.id, getBalance]);

  const l1Erc20TokenBalanceRaw: bigint = (l1Erc20TokenBalance as bigint) || 0n;
  const l1Erc20TokenBalanceHuman = convertBigIntToString(l1Erc20TokenBalanceRaw, token);

  const usdVal = Big(l1Erc20TokenBalanceHuman).mul(getTokenUsdPrice(token)).toString();

  return () => ({
    balanceToken: l1Erc20TokenBalanceRaw,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    isLoadingBalance: isLoadingErc20Balance,
    isConnected,
    address,
    connect: () => Promise.resolve(connectEvm()),
    disconnect,
  });
}

function useTrxTokenWalletStrategy(token: Currency): () => Wallet {
  const { getTokenUsdPrice } = useTokenUsdPrices();
  const { address: tronAddress, isConnected, connect, disconnect, tronWeb } = useTronWallet();
  const { getContractParams } = useL1TokenContractParams();
  const {
    data: trc20TokenBalance,
    getBalance: getTrc20Balance,
    isLoading: isLoadingTrc20Balance,
  } = useReadTRC20Balance(tronAddress);

  useEffect(() => {
    if (isTrc20Currency(token)) {
      const { L1_TOKEN_ADDRESS } = getContractParams(token);
      getTrc20Balance(L1_TOKEN_ADDRESS);
    }
  }, [token, getContractParams, getTrc20Balance]);

  const trc20TokenBalanceRaw: bigint = BigInt(trc20TokenBalance || 0n);
  const trc20TokenBalanceHuman = convertBigIntToString(trc20TokenBalanceRaw, token);

  const usdVal = Big(trc20TokenBalanceHuman).mul(getTokenUsdPrice(token)).toString();

  return () => ({
    tronWeb,
    balanceToken: trc20TokenBalanceRaw,
    balanceUsd: formatNumberWithPrecision(usdVal, BALANCE_FIAT_PRECISION),
    isLoadingBalance: isLoadingTrc20Balance,
    isConnected,
    address: tronAddress,
    connect,
    disconnect,
  });
}

export function useWallet<TCurrency extends Currency>(currency: TCurrency): Wallet<TCurrency> {
  const getBtcWallet = useBtcWalletStrategy();
  const getEvmWallet = useEvmWalletStrategy(currency);
  const getEvmTokenWallet = useEvmTokenWalletStrategy(currency);
  const getTrxTokenWallet = useTrxTokenWalletStrategy(currency);
  const getTronWallet = useTronWalletStrategy();
  const getLtcWallet = useLtcWalletStrategy();
  const getDogeWallet = useDogeWalletStrategy();
  const getCwebWallet = useCwebWalletStrategy();

  if (isBtcCurrency(currency)) {
    return getBtcWallet() as Wallet<TCurrency>;
  }
  if (isLtcCurrency(currency)) {
    return getLtcWallet() as Wallet<TCurrency>;
  }
  if (isDogeCurrency(currency)) {
    return getDogeWallet() as Wallet<TCurrency>;
  }
  if (isEvmCurrency(currency)) {
    return getEvmWallet() as Wallet<TCurrency>;
  }
  if (isCwebCurrency(currency)) {
    return getCwebWallet() as Wallet<TCurrency>;
  }
  if (isTronCurrency(currency)) {
    return getTronWallet() as Wallet<TCurrency>;
  }
  if (isErc20Currency(currency)) {
    return getEvmTokenWallet() as Wallet<TCurrency>;
  }
  if (isTrc20Currency(currency)) {
    return getTrxTokenWallet() as Wallet<TCurrency>;
  }

  // Fallback for any other token types
  return getEvmTokenWallet() as Wallet<TCurrency>;
}
